// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the people of a meeting (owner, 2026-10-12), beside the
 * activities (`meetingActivitiesUI.ts`, which mounts this and hands it the
 * room's status and messages):
 *
 *   - **Your name** (everyone in the audience, on a computer, a phone and
 *     in phone AR; in a headset it is asked on the page before entering):
 *     a small card, offered once when they arrive and from the pill with
 *     their name after. Optional. The room keeps it with their answers
 *     until the meeting ends.
 *   - **My answers** (each participant): their own answers so far, kept by
 *     the room until the meeting ends — a reload or a dropped connection
 *     loses nothing.
 *   - **People** (the presenter and the moderators): who is in the meeting
 *     ("Guest 3" without a name), hands up, who drives a globe and whose
 *     microphone is on; Give the globe (or another globe), Take back, Mic
 *     off, and Remove (they cannot come back to this meeting). Its
 *     **Answers** tab lists the meeting's activities and, for one, each
 *     person's answer (a poll's never: a poll is anonymous).
 *
 *   - **End meeting** (the presenter) and **Leave** (everyone else), each
 *     asked once more: ending deletes everything the meeting kept and
 *     closes it for everyone; leaving closes it for this person only.
 *
 * The windows are dragged like the activity sheet (`makeDraggable`).
 * Lives in `src/ui/` so `check:i18n-strings` covers it.
 */

import { t } from '../i18n'
import type { ActivityData, RoomStatus } from '../services/roomClient'
import {
  NAME_CHARS,
  distanceKm,
  type Given,
  type LoggedActivity,
  type LogRow,
  type MyAnswer,
  type RoomPerson,
} from '../services/roomActivity'
import type { ActivityRoom } from './meetingActivitiesUI'

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text) node.textContent = text
  return node
}

function button(className: string, text: string): HTMLButtonElement {
  const b = el('button', className, text)
  b.type = 'button'
  // Taps on the overlay must not reach the AR session as a select.
  b.addEventListener('beforexrselect', (ev) => ev.preventDefault())
  return b
}

const show = (node: HTMLElement, on: boolean): void => {
  node.classList.toggle('hidden', !on)
}

/** A place as people read it: 12.3° N, 45.6° W. */
export function formatPlace([lat, lon]: readonly [number, number]): string {
  const ns = lat >= 0 ? t('activity.answers.north') : t('activity.answers.south')
  const ew = lon >= 0 ? t('activity.answers.east') : t('activity.answers.west')
  return `${Math.abs(lat).toFixed(1)}° ${ns}, ${Math.abs(lon).toFixed(1)}° ${ew}`
}

/** What someone gave in an activity, in words: a place (and how far it was), an option, options, or pins. */
export function formatGiven(activity: LoggedActivity, given: Given): string {
  if (given.p) {
    const place = formatPlace(given.p)
    return activity.answer ? `${place} · ${t('activity.answers.km', { km: Math.round(distanceKm(given.p, activity.answer)).toLocaleString() })}` : place
  }
  const option = (i: number): string => activity.options?.[i] ?? '—'
  if (given.choices) return given.choices.map(option).join(', ')
  if (given.choice !== undefined) return option(given.choice)
  if (given.pins) {
    const notes = given.pins.map((p) => p.note).filter(Boolean)
    return [t('activity.answers.pins', { n: given.pins.length }), ...notes].join(' · ')
  }
  return '—'
}

export interface PeopleUI {
  /** The room's status changed. */
  update(status: RoomStatus): void
  /** People, log, answers, mine: from the room. */
  data(d: ActivityData): void
  /** Open the name card (the audience's "Add your name"). */
  askName(): void
  dispose(): void
}

/**
 * Mount the name card, My answers and the People window. `row` is the
 * activities' top row, where the pills go; `drag` makes a window movable
 * (the activity sheet's `makeDraggable`).
 */
export function mountMeetingPeople(
  root: HTMLElement,
  row: HTMLElement,
  room: ActivityRoom,
  opts: {
    code: string
    drag: (panel: HTMLElement, head: HTMLElement) => () => void
    /** The globes on the presenter's page, so another one can be given. */
    globes?: () => { count: number; primary: number }
    /** While a game is on the room sends no typed names: a person is shown by their game name. */
    gameNameOf?: (connId: string) => string | null
    /** This person leaves the meeting (the page lets go of the room). */
    onLeave?: () => void
  },
): PeopleUI {
  const nameKey = `ayni-room-name:${opts.code}`
  const askedKey = `ayni-room-name-asked:${opts.code}`
  const session = {
    get(key: string): string | null {
      try {
        return sessionStorage.getItem(key)
      } catch {
        return null
      }
    },
    set(key: string, value: string | null): void {
      try {
        if (value === null) sessionStorage.removeItem(key)
        else sessionStorage.setItem(key, value)
      } catch {
        // Kept for this page only.
      }
    },
  }

  let status: RoomStatus | null = null
  let people: RoomPerson[] = []
  let log: LoggedActivity[] = []
  let mine: MyAnswer[] = []
  /** The activity whose answers the Answers tab shows, and its rows once they come. */
  let opened: { id: string; rows: LogRow[] | null } | null = null
  let tab: 'people' | 'answers' = 'people'
  /** The person whose removal is being confirmed. */
  let removing: string | null = null

  const audience = (): boolean => !!status?.meeting && status.connected && status.seat !== 'presenter' && status.seat !== 'moderator'
  const manages = (): boolean => !!status?.meeting && status.connected && (status.seat === 'presenter' || status.seat === 'moderator')
  const labelOf = (p: { id?: string; n: number; name: string | null; seat?: string }): string =>
    p.name ?? (p.id ? opts.gameNameOf?.(p.id) : null) ?? (p.seat === 'moderator' ? t('activity.people.moderatorLabel') : t('activity.people.guest', { n: p.n }))

  // ------------------------------------------------------------- pills
  const namePill = button('activity-start activity-name-pill hidden', '')
  namePill.setAttribute('aria-haspopup', 'dialog')
  const minePill = button('activity-start activity-mine-pill hidden', t('activity.mine.pill'))
  minePill.setAttribute('aria-haspopup', 'dialog')
  const peoplePill = button('activity-start activity-people-pill hidden', '')
  peoplePill.setAttribute('aria-haspopup', 'dialog')
  // AYNI (owner, 2026-10-12): a way out — End meeting for the presenter, Leave for everyone else.
  const exitPill = button('activity-start activity-exit-pill hidden', '')
  exitPill.setAttribute('aria-haspopup', 'dialog')
  row.append(peoplePill, namePill, minePill, exitPill)

  function sheet(className: string, title: string): { panel: HTMLDivElement; body: HTMLDivElement; off: () => void } {
    const panel = el('div', `activity-sheet ${className} hidden`)
    panel.setAttribute('role', 'dialog')
    panel.setAttribute('aria-label', title)
    const head = el('div', 'activity-sheet-header')
    const close = button('activity-sheet-close', '✕')
    close.setAttribute('aria-label', t('activity.sheet.close'))
    close.addEventListener('click', () => show(panel, false))
    head.append(el('h2', '', title), close)
    const body = el('div', 'activity-sheet-body')
    panel.append(head, body)
    panel.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation()
        show(panel, false)
      }
    })
    root.append(panel)
    return { panel, body, off: opts.drag(panel, head) }
  }

  // ------------------------------------------------------------- your name
  const nameSheet = sheet('activity-name-card', t('activity.name.title'))
  const nameHint = el('p', 'activity-meta', t('activity.name.hint'))
  const nameInput = el('input')
  nameInput.type = 'text'
  nameInput.maxLength = NAME_CHARS
  nameInput.setAttribute('aria-label', t('activity.name.title'))
  nameInput.placeholder = t('activity.name.title')
  const nameSave = button('activity-primary', t('activity.name.save'))
  const nameSkip = button('', t('activity.name.skip'))
  const nameActions = el('div', 'activity-actions')
  nameActions.append(nameSave, nameSkip)
  nameSheet.body.append(nameHint, nameInput, nameActions)

  function askName(): void {
    nameInput.value = session.get(nameKey) ?? ''
    show(nameSheet.panel, true)
    session.set(askedKey, '1')
    nameInput.focus()
  }
  function saveName(): void {
    const name = nameInput.value.trim().slice(0, NAME_CHARS) || null
    session.set(nameKey, name)
    room.send({ t: 'name', name })
    show(nameSheet.panel, false)
    render()
  }
  nameSave.addEventListener('click', saveName)
  nameInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') saveName()
  })
  nameSkip.addEventListener('click', () => show(nameSheet.panel, false))
  namePill.addEventListener('click', () => (nameSheet.panel.classList.contains('hidden') ? askName() : show(nameSheet.panel, false)))

  // ------------------------------------------------------------- my answers
  const mineSheet = sheet('activity-mine', t('activity.mine.title'))
  function renderMine(): void {
    mineSheet.body.replaceChildren(
      el('p', 'activity-meta', t('activity.mine.note')),
      ...[...mine].reverse().map(({ activity, given }) => {
        const item = el('div', 'activity-answer-item')
        item.append(el('strong', '', activity.prompt), el('span', '', formatGiven(activity, given)))
        return item
      }),
    )
  }
  minePill.addEventListener('click', () => {
    const open = mineSheet.panel.classList.contains('hidden')
    if (open) renderMine()
    show(mineSheet.panel, open)
  })

  // ------------------------------------------------------------- people
  const peopleSheet = sheet('activity-people', t('activity.people.title'))
  const tabs = el('div', 'activity-tabs')
  tabs.setAttribute('role', 'tablist')
  const peopleTab = button('activity-tab', t('activity.people.tab.people'))
  const answersTab = button('activity-tab', t('activity.people.tab.answers'))
  for (const b of [peopleTab, answersTab]) b.setAttribute('role', 'tab')
  tabs.append(peopleTab, answersTab)
  const peopleList = el('div', 'activity-people-list')
  peopleSheet.body.append(tabs, peopleList)
  peopleTab.addEventListener('click', () => {
    tab = 'people'
    renderPeople()
  })
  answersTab.addEventListener('click', () => {
    tab = 'answers'
    renderPeople()
  })
  peoplePill.addEventListener('click', () => {
    const open = peopleSheet.panel.classList.contains('hidden')
    removing = null
    show(peopleSheet.panel, open)
    if (open) renderPeople()
  })

  function personRow(p: RoomPerson): HTMLElement {
    const item = el('div', 'activity-person')
    const who = el('div', 'activity-person-who')
    who.append(el('strong', '', labelOf(p)))
    const badges: string[] = []
    if (p.seat === 'moderator' && p.name) badges.push(t('activity.people.moderator'))
    if (p.hand) badges.push(t('activity.people.hand'))
    if (p.drives === 'main') badges.push(t('activity.people.drivesMain'))
    else if (p.drives !== null) badges.push(t('activity.people.drivesGlobe', { n: p.drives + 1 }))
    if (p.mic) badges.push(t('activity.people.micOn'))
    if (badges.length) who.append(el('span', 'activity-person-badges', badges.join(' · ')))
    const actions = el('div', 'activity-actions')
    if (removing === p.id) {
      actions.append(el('span', 'activity-person-confirm', t('activity.people.removeConfirm', { name: labelOf(p) })))
      const yes = button('activity-primary activity-danger', t('activity.people.removeYes'))
      yes.addEventListener('click', () => {
        room.send({ t: 'kick', id: p.id })
        removing = null
        renderPeople()
      })
      const no = button('', t('activity.people.removeNo'))
      no.addEventListener('click', () => {
        removing = null
        renderPeople()
      })
      actions.append(yes, no)
      item.append(who, actions)
      return item
    }
    if (p.drives === null) {
      const give = button('activity-primary', t('activity.hands.give'))
      give.addEventListener('click', () => room.send({ t: 'spotlight', id: p.id }))
      actions.append(give)
      const globes = opts.globes?.() ?? { count: 1, primary: 0 }
      for (let slot = 0; slot < globes.count; slot++) {
        if (slot === globes.primary) continue
        const giveGlobe = button('activity-give-globe', t('activity.hands.giveGlobe', { n: slot + 1 }))
        giveGlobe.addEventListener('click', () => room.send({ t: 'drive', slot, id: p.id }))
        actions.append(giveGlobe)
      }
    } else {
      const back = button('activity-primary', t('activity.spotlight.takeBack'))
      const slot = p.drives
      back.addEventListener('click', () => room.send(slot === 'main' ? { t: 'spotlight', id: null } : { t: 'drive', slot, id: null }))
      actions.append(back)
    }
    if (p.mic) {
      const mute = button('', t('activity.people.mute'))
      mute.addEventListener('click', () => room.send({ t: 'mute', id: p.id }))
      actions.append(mute)
    }
    // A moderator removes participants; the presenter, anyone.
    if (status?.seat === 'presenter' || p.seat === 'audience') {
      const remove = button('activity-danger-link', t('activity.people.remove'))
      remove.addEventListener('click', () => {
        removing = p.id
        renderPeople()
      })
      actions.append(remove)
    }
    item.append(who, actions)
    return item
  }

  function answersView(): HTMLElement[] {
    if (opened) {
      const a = log.find((x) => x.id === opened!.id)
      const back = button('activity-link', `← ${t('activity.answers.back')}`)
      back.addEventListener('click', () => {
        opened = null
        renderPeople()
      })
      if (!a) return [back]
      const out: HTMLElement[] = [back, el('h3', 'activity-answers-title', a.prompt)]
      if (a.kind === 'poll') {
        out.push(el('p', 'activity-meta', t('activity.answers.pollAnon')))
        return out
      }
      if (!opened.rows) return [...out, el('p', 'activity-meta', t('activity.answers.loading'))]
      if (!opened.rows.length) return [...out, el('p', 'activity-meta', t('activity.answers.nobody'))]
      for (const r of opened.rows) {
        const item = el('div', 'activity-answer-item')
        item.append(el('strong', '', labelOf(r)), el('span', '', formatGiven(a, r.given)))
        out.push(item)
      }
      return out
    }
    if (!log.length) return [el('p', 'activity-meta', t('activity.answers.none'))]
    return [...log].reverse().map((a) => {
      const b = button('activity-log-item', '')
      b.append(
        el('span', 'activity-log-kind', t(`activity.answers.kind.${a.kind}`)),
        el('strong', '', a.prompt),
        el('span', 'activity-meta', t('activity.answers.count', { n: a.answered })),
      )
      b.addEventListener('click', () => {
        opened = { id: a.id, rows: null }
        if (a.kind !== 'poll') room.send({ t: 'logAsk', id: a.id })
        renderPeople()
      })
      return b
    })
  }

  function renderPeople(): void {
    peopleTab.setAttribute('aria-selected', String(tab === 'people'))
    answersTab.setAttribute('aria-selected', String(tab === 'answers'))
    peopleTab.classList.toggle('selected', tab === 'people')
    answersTab.classList.toggle('selected', tab === 'answers')
    if (tab === 'answers') {
      peopleList.replaceChildren(...answersView())
      return
    }
    peopleList.replaceChildren(...(people.length ? people.map(personRow) : [el('p', 'activity-meta', t('activity.people.empty'))]))
  }

  // ------------------------------------------------------------- end / leave
  const exitSheet = sheet('activity-exit', t('activity.exit.title'))
  const exitText = el('p', 'activity-meta')
  const exitYes = button('activity-primary activity-danger', '')
  const exitNo = button('', t('activity.people.removeNo'))
  const exitActions = el('div', 'activity-actions')
  exitActions.append(exitYes, exitNo)
  exitSheet.body.append(exitText, exitActions)
  const presenting = (): boolean => status?.seat === 'presenter'
  exitPill.addEventListener('click', () => {
    const open = exitSheet.panel.classList.contains('hidden')
    exitText.textContent = t(presenting() ? 'activity.exit.endConfirm' : 'activity.exit.leaveConfirm')
    exitYes.textContent = t(presenting() ? 'activity.exit.end' : 'activity.exit.leave')
    show(exitSheet.panel, open)
    if (open) exitNo.focus()
  })
  exitNo.addEventListener('click', () => show(exitSheet.panel, false))
  exitYes.addEventListener('click', () => {
    show(exitSheet.panel, false)
    if (presenting()) room.send({ t: 'endMeeting' })
    else opts.onLeave?.()
  })

  // ------------------------------------------------------------- state
  let offeredName = false
  function render(): void {
    const inMeeting = !!status?.meeting && !!status.connected && !status.kicked && !status.ended
    const name = session.get(nameKey)
    show(namePill, inMeeting && audience())
    namePill.textContent = name ? t('activity.name.pill', { name }) : t('activity.name.add')
    show(minePill, inMeeting && audience() && mine.length > 0)
    show(peoplePill, inMeeting && manages())
    peoplePill.textContent = t('activity.people.pill', { n: people.length })
    // Leave needs somewhere to go: offered where the page can let go of the room.
    show(exitPill, inMeeting && (presenting() || !!opts.onLeave))
    exitPill.textContent = t(presenting() ? 'activity.exit.end' : 'activity.exit.leave')
    if (!inMeeting) show(exitSheet.panel, false)
    if (!(inMeeting && audience())) {
      show(nameSheet.panel, false)
      show(mineSheet.panel, false)
    }
    if (!(inMeeting && manages())) show(peopleSheet.panel, false)
    // Arriving in the audience with no name in this tab: offered once, never forced.
    if (inMeeting && audience() && !name && !offeredName && !session.get(askedKey)) {
      offeredName = true
      askName()
    }
  }

  return {
    update(next) {
      status = next
      // The room has a name for this person (another tab of this browser gave it): this tab shows it too.
      if (next.connected && next.me?.name && !session.get(nameKey)) session.set(nameKey, next.me.name)
      render()
    },
    data(d) {
      if (d.t === 'people') {
        people = d.people
        if (!peopleSheet.panel.classList.contains('hidden') && tab === 'people' && removing === null) renderPeople()
      } else if (d.t === 'log') {
        log = d.acts
        if (!peopleSheet.panel.classList.contains('hidden') && tab === 'answers') renderPeople()
      } else if (d.t === 'logRows') {
        if (opened?.id === d.id) {
          opened.rows = d.rows
          if (tab === 'answers') renderPeople()
        }
      } else if (d.t === 'mine') {
        mine = d.mine
        if (!mineSheet.panel.classList.contains('hidden')) renderMine()
      }
      render()
    },
    askName,
    dispose() {
      nameSheet.off()
      mineSheet.off()
      peopleSheet.off()
      exitSheet.off()
      for (const node of [namePill, minePill, peoplePill, exitPill, nameSheet.panel, mineSheet.panel, peopleSheet.panel, exitSheet.panel]) node.remove()
    },
  }
}
